import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'

import * as Apollo from '@apollo/client'
import { useQuery } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Pencil, Share } from '@factorialco/f0-react/icons/app'
import { ResultOf } from '@graphql-typed-document-node/core'
import {
  CompensationsListColumnsDocument,
  CompensationsListColumnsQuery,
  CompensationsListColumnsQueryVariables,
  CompensationsListFragment,
  CompensationsValueObjectsEmployeeCompensationValidationTypeEnum,
  EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment,
  EmployeePayrollRunInputRowPayrollRunCompensationFragment,
  EmployeePayrollRunTotalsFragment,
  EmployeePayrollRunsDocument,
  ValidationTypeFiltersDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  EmployeePayrollRunNode,
  employeePayrollRunElement,
  formatCellValue,
} from 'modules/compensations/components/EmployeePayrollRunElements'
import { useCompensationsListFilterOptions } from 'modules/compensations/hooks/useCompensationsListFilterOptions'
import { useCompensationsRefresh } from 'modules/compensations/hooks/useCompensationsRefresh'
import { useCurrencyContext } from 'modules/compensations/lib/CurrencyContext'
import { usePayrollRuns } from 'modules/compensations/lib/PayrollRunsContext'
import {
  calculateTotalAlerts,
  createCountItemsWithAlerts,
  processCompensationsFilters,
  renderAlertsColumn,
  renderEmployeeColumn,
} from 'modules/compensations/lib/compensationsListHelpers'
import { employeeStatusFilterConfig } from 'modules/compensations/types/filters'
import { useIntegrationsSyncContext } from 'modules/integrations/public'
// TODO: We should avoid coupling modules because it hurts application performance - in order to execute this
//  compensations page, browser will have to upload the payroll_bundle as well, so, a good approach would be to
//  move this logic to the core or potentially to the compensations module. When deprecating the old payroll product we'll
//  have to migrate payroll concepts inside Compensation and this logic as well, but until then the same concept will
//  work for different products
import {
  getUnits,
  isUnitKey,
} from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

import { getHeaderOptions } from './utils'

/**
 * __useCompensationsListColumnsQuery__
 *
 * To run a query within a React component, call `useCompensationsListColumnsQuery` and pass it any options that fit your needs.
 * When your component renders, `useCompensationsListColumnsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCompensationsListColumnsQuery({
 *   variables: {
 *      ids: // array of 'payrollRunIds'
 *   },
 * });
 */
function useCompensationsListColumnsQuery(
  baseOptions: Apollo.QueryHookOptions<
    CompensationsListColumnsQuery,
    CompensationsListColumnsQueryVariables
  >
) {
  const options = { ...baseOptions }
  return Apollo.useQuery<CompensationsListColumnsQuery, CompensationsListColumnsQueryVariables>(
    CompensationsListColumnsDocument,
    options
  )
}

type CompensationsListProps = {
  payrollRun: CompensationsListFragment
  onEdit: (employeePayrollRunInputRowEmployeePayrollRun: EmployeePayrollRunNode) => void
  locale: string
  refreshFnRef: React.MutableRefObject<(() => void) | null>
}

type CompensationsListFilters = {
  companyId: number
  featureFlagNames: string[]
  payrollRunId: number
  legalEntityIds?: number[]
  teamIds?: number[]
  workplaceIds?: number[]
  validationTypes?: CompensationsValueObjectsEmployeeCompensationValidationTypeEnum[]
}

function CompensationsList({ payrollRun, onEdit, locale, refreshFnRef }: CompensationsListProps) {
  const { navigation } = useNavigation()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [currency] = useCurrencyContext()

  const [filters, setCompensationsFilters] = useState<CompensationsListFilters | undefined>()

  const { refreshKey } = useCompensationsRefresh({ refreshFnRef })

  const { data } = useCompensationsListColumnsQuery({
    variables: filters,
    skip: !filters,
  })

  const { marketplaceIntegration } = useIntegrationsSyncContext()

  const { currentPayrollRun, payrollRuns, setCurrentPayrollRun } = usePayrollRuns()

  const { data: validationFiltersData } = useQuery(ValidationTypeFiltersDocument, {
    variables: {
      payrollRunId: payrollRun.id,
    },
  })

  const validationFiltersDataResult = validationFiltersData?.compensations

  const { loadLegalEntityOptions, loadTeamOptions, loadWorkplaceOptions } =
    useCompensationsListFilterOptions(companyId)

  const countItemsWithAlerts = useMemo(
    () => createCountItemsWithAlerts(validationFiltersDataResult),
    [validationFiltersDataResult]
  )

  const totalAlerts = useMemo(
    () => calculateTotalAlerts(validationFiltersDataResult),
    [validationFiltersDataResult]
  )

  const renderEmployeeColumnBound = useCallback(
    (item: EmployeePayrollRunNode) => renderEmployeeColumn(item),
    []
  )

  const headerElements = useMemo(
    () => data?.compensations.elementPayrollConceptsConnection.nodes ?? [],
    [data?.compensations.elementPayrollConceptsConnection.nodes]
  )

  const hasPayrollResultConcepts = useMemo(
    () =>
      headerElements.some(
        (element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) =>
          element.resultType === 'payroll_result'
      ),
    [headerElements]
  )

  const conceptColumns = useMemo(() => {
    const unitsConfiguration = getUnits({ money: currency.isoCode })

    return headerElements.map(
      (element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) => {
        const options = hasPayrollResultConcepts ? getHeaderOptions(element.resultType) : null
        const elementPayrollConceptId = element.payrollConceptId
        const elementResultType = element.resultType

        return {
          label: element.label,
          summary: element.id,
          infoIcon: options?.infoIcon,
          info: options?.info,
          render: (item: EmployeePayrollRunNode) => {
            const employeePayrollRunCompensation = item?.compensations?.find(
              (comp: EmployeePayrollRunElementInputEmployeePayrollRunCompensationFragment) =>
                comp.payrollConcept?.id === elementPayrollConceptId &&
                comp.resultType === elementResultType
            )

            return employeePayrollRunElement({
              employeePayrollElement: employeePayrollRunCompensation,
              locale,
              unitsConfiguration,
            })
          },
        }
      }
    )
  }, [locale, headerElements, hasPayrollResultConcepts, currency.isoCode])

  const createPayrollTotalsMap = useCallback(
    (
      payrollRunTotals: EmployeePayrollRunTotalsFragment[],
      headerColumns: EmployeePayrollRunInputRowPayrollRunCompensationFragment[],
      currencyIsoCode?: string
    ): Record<string, string> => {
      const result: Record<string, string> = {}

      headerColumns.forEach((column) => {
        result[column.id] = '-'
      })

      const unitsConfig = getUnits({ money: currencyIsoCode })

      payrollRunTotals.forEach((total) => {
        const totalElement = headerColumns.find((e) => e.id === total.id)

        if (totalElement?.name && total.totalAmountInCents != null) {
          const unit = totalElement.payrollConceptUnit

          if (unit && isUnitKey(unit)) {
            const unitSymbol = unitsConfig[unit]

            result[totalElement.id] = formatCellValue(
              unit,
              unitSymbol,
              Number(total.totalAmountInCents),
              locale
            )
          }
        }
      })

      return result
    },
    [locale]
  )

  const summaries = useMemo(() => {
    const result: { [key: string]: { type: 'sum' } } = {}

    headerElements.forEach((element: EmployeePayrollRunInputRowPayrollRunCompensationFragment) => {
      result[element.id] = {
        type: 'sum',
      }
    })

    return result
  }, [headerElements])

  const [defaultDate] = useState<Date>(() =>
    currentPayrollRun ? new Date(currentPayrollRun.startsOn) : new Date()
  )

  const dateRange = useMemo(() => {
    const startsOnDates = payrollRuns.map((run) => new Date(run.startsOn))
    const endsOnDates = payrollRuns.map((run) => new Date(run.endsOn))

    return {
      minDate: new Date(Math.min(...startsOnDates.map((d) => d.getTime()))),
      maxDate: new Date(Math.max(...endsOnDates.map((d) => d.getTime()))),
    }
  }, [payrollRuns])

  const mapSummariesFn = useCallback(
    (queryData: ResultOf<typeof EmployeePayrollRunsDocument>) => {
      return queryData.compensations.payrollRunEmployeesConnection.nodes[0]
        ? {
            ...queryData.compensations.payrollRunEmployeesConnection.nodes[0],
            ...createPayrollTotalsMap(
              queryData.compensations.payrollRunTotalsConnection.nodes ?? [],
              headerElements,
              currency.isoCode
            ),
          }
        : undefined
    },
    [createPayrollTotalsMap, headerElements, currency.isoCode]
  )

  const source = useGraphqlDataCollectionSource(
    {
      document: EmployeePayrollRunsDocument,
      fetchPolicy: 'network-only',
      mapData: (queryData) => queryData.compensations.payrollRunEmployeesConnection.nodes,
      totalItemSummary: (totalItems) =>
        totalItems === 1
          ? i18n.t('compensations.common.employees.one', { totalItems })
          : i18n.t('compensations.common.employees.other', {
              totalItems,
            }),
      paginationType: 'infinite-scroll',
      summaries,
      mapSummaries: mapSummariesFn,
      mapPagination: (queryData) => {
        return {
          total: queryData.compensations.payrollRunEmployeesConnection.totalCount ?? 0,
          cursor: queryData.compensations.payrollRunEmployeesConnection.pageInfo.endCursor ?? '0',
          hasNextPage:
            queryData.compensations.payrollRunEmployeesConnection.pageInfo.hasNextPage ?? false,
        }
      },
      mapVariables: ({ navigationFilters, sortings, search, filters: collectionFilters }) => {
        const { legalEntityIds, teamIds, workplaceIds, validationTypes, employeeStatus } =
          processCompensationsFilters(collectionFilters)

        // TODO: this is a hack to set the filters in the parent component until
        // the DataOneCollection is updated to support columns dynamically
        const currentFilters = {
          companyId,
          featureFlagNames: [],
          payrollRunId: payrollRun.id,
          legalEntityIds,
          teamIds,
          workplaceIds,
          validationTypes,
        }

        setCompensationsFilters(currentFilters)

        if (!navigationFilters?.date?.value?.from || !navigationFilters?.date?.value?.to) {
          return {
            payrollRunId: payrollRun.id,
            search: search,
            sort: sortings?.[0],
            legalEntityIds,
            teamIds,
            workplaceIds,
            marketplaceIntegrationId: marketplaceIntegration?.id || 0,
            includeSyncableState: !!marketplaceIntegration,
            validationTypes,
            employeeStatus,
            companyId,
          }
        }

        const now = moment()
        const from = moment(navigationFilters?.date.value.from) || now.startOf('month')
        const to = moment(navigationFilters?.date.value.to) || now.endOf('month')
        const payrollRunId = payrollRuns.find((element) => {
          return (
            moment(element.startsOn).isSameOrAfter(from) &&
            moment(element.endsOn).isSameOrBefore(to)
          )
        })?.id

        if (payrollRunId && payrollRunId !== currentPayrollRun?.id) {
          setCurrentPayrollRun(Number(payrollRunId))
        }

        return {
          payrollRunId: payrollRunId || payrollRun.id,
          search: search,
          sortOrder: sortings?.[0],
          legalEntityIds,
          teamIds,
          workplaceIds,
          marketplaceIntegrationId: marketplaceIntegration?.id || 0,
          includeSyncableState: !!marketplaceIntegration,
          validationTypes,
          employeeStatus,
          companyId,
        }
      },
      navigationFilters: {
        date: {
          type: 'date-navigator',
          defaultValue: defaultDate,
          granularity: 'month',
          max: dateRange.maxDate,
          min: dateRange.minDate,
          hideGoToCurrent: true,
        },
      },
      search: {
        enabled: true,
      },
      defaultSortings: {
        field: 'fullName',
        order: 'asc',
      },
      filters: {
        legalEntityIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.legalEntityColumnLabel'),
          type: 'in',
          options: {
            cache: false,
            options: () => loadLegalEntityOptions(payrollRun),
          },
        },
        teamIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.teamColumnLabel'),
          type: 'in',
          options: {
            cache: true,
            options: loadTeamOptions,
          },
        },
        workplaceIds: {
          label: i18n.t('compensations.PayrollRunCompensationsList.filters.workplaceColumnLabel'),
          type: 'in',
          options: {
            cache: true,
            options: loadWorkplaceOptions,
          },
        },
        ...{
          validationTypes: {
            label: i18n.t(
              'compensations.PayrollRunCompensationsList.filters.validationTypeColumnLabel'
            ),
            type: 'in',
            options: {
              options: [
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.pendingAlertColumnLabel'
                  ),
                  value: 'pending_to_define',
                },
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.warningAlertColumnLabel'
                  ),
                  value: 'above_limit',
                },
                {
                  label: i18n.t(
                    'compensations.PayrollRunCompensationsList.filters.discrepancyAlertColumnLabel'
                  ),
                  value: 'discrepancy',
                },
              ].filter((option) => option && option.value != null),
            },
          },
        },
        ...employeeStatusFilterConfig(),
      },
      presets: [
        ...(countItemsWithAlerts('above_limit')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.warningAlertColumnLabel'
                ),
                filter: { validationTypes: ['above_limit'] },
                itemsCount: countItemsWithAlerts('above_limit'),
              },
            ]
          : []),
        ...(countItemsWithAlerts('pending_to_define')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.pendingAlertColumnLabel'
                ),
                filter: { validationTypes: ['pending_to_define'] },
                itemsCount: countItemsWithAlerts('pending_to_define'),
              },
            ]
          : []),
        ...(countItemsWithAlerts('discrepancy')() > 0
          ? [
              {
                label: i18n.t(
                  'compensations.PayrollRunCompensationsList.filters.discrepancyAlertColumnLabel'
                ),
                filter: { validationTypes: ['discrepancy'] },
                itemsCount: countItemsWithAlerts('discrepancy'),
              },
            ]
          : []),
      ],
      itemOnClick: (item: EmployeePayrollRunNode) => () => onEdit(item),
      itemActions: (item: EmployeePayrollRunNode) => [
        {
          label: i18n.t('compensations.PayrollRunCompensationsList.updateActionLabel'),
          icon: Pencil,
          onClick: () => {
            item.id && onEdit(item)
          },
        },
        {
          label: i18n.t('compensations.PayrollRunCompensationsList.goToContractActionLabel'),
          icon: Share,
          onClick: () => {
            item.employee?.id &&
              window.open(
                navigation.employees.id.contractVersions.fullPath({
                  id: String(item.employee.id),
                }),
                '_blank'
              )
          },
        },
      ],
    },
    [
      defaultDate,
      dateRange,
      payrollRuns,
      currentPayrollRun,
      setCurrentPayrollRun,
      navigation,
      onEdit,
      locale,
      headerElements,
      refreshKey,
      countItemsWithAlerts,
    ]
  )

  return (
    <OneDataCollection
      fullHeight
      emptyStates={{
        'no-data': {
          title: i18n.t('compensations.employeePayrollRunElementCell.empty'),
        },
        'no-results': {
          title: i18n.t('compensations.employeePayrollRunElementCell.empty_results.title'),
          description: i18n.t(
            'compensations.employeePayrollRunElementCell.empty_results.description'
          ),
          emoji: '🧐',
        },
      }}
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('compensations.PayrollRunCompensationsList.employeeColumnLabel'),
                sorting: 'fullName',
                render: renderEmployeeColumnBound,
              },
              ...(totalAlerts > 0
                ? [
                    {
                      label: i18n.t('compensations.PayrollRunCompensationsList.alertsColumnLabel'),
                      info: i18n.t('compensations.PayrollRunCompensationsList.alertsColumnTooltip'),
                      render: renderAlertsColumn,
                    },
                  ]
                : []),
              ...conceptColumns,
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}

export { CompensationsList }
